import React, { useState } from 'react';
import { Lock, Eye, EyeOff, FileText, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const styles = {
  container: {
    padding: '40px 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    animation: 'fadeUp 0.4s ease both',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '24px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '640px',
    boxShadow: '0 20px 50px rgba(0,0,0,0.05)',
    textAlign: 'center',
  },
  topIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: '#fef2f2',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: '8px',
  },
  sub: {
    fontSize: '16px',
    color: '#64748b',
    marginBottom: '32px',
  },
  divider: {
    height: '1px',
    background: '#f1f5f9',
    width: '100%',
    marginBottom: '32px',
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    background: '#f8fafc',
    border: '1px solid #f1f5f9',
    borderRadius: '12px',
    marginBottom: '32px',
    textAlign: 'left',
  },
  fileIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ef4444',
  },
  fileName: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#0f172a',
  },
  fileType: {
    fontSize: '13px',
    color: '#94a3b8',
    marginTop: '2px',
  },
  inputArea: {
    textAlign: 'left',
  },
  inputLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: '10px',
  },
  inputRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  inputWrapper: {
    position: 'relative',
    flex: 1,
  },
  input: {
    width: '100%',
    height: '52px',
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    borderRadius: '12px',
    padding: '0 44px 0 44px',
    fontSize: '16px',
    color: '#0f172a',
    transition: 'all 0.2s',
  },
  lockIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
  },
  eyeBtn: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
  },
  unlockBtn: {
    height: '52px',
    padding: '0 28px',
    borderRadius: '12px',
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    fontWeight: 700,
    fontSize: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
  },
  footerHint: {
    marginTop: '24px',
    padding: '16px',
    background: '#f5f3ff',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#4338ca',
    fontSize: '13px',
    textAlign: 'left',
    textDecoration: 'none',
    transition: 'background 0.2s',
  },
};

export default function PDFUnlock({ file, onPasswordChange, onUnlock }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handlePass = (val) => {
    setPassword(val);
    onPasswordChange(val);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.topIcon}>
          <FileText size={32} />
        </div>
        
        <h1 style={styles.title}>This file is password protected</h1>
        <p style={styles.sub}>Please enter the password to view the PDF file.</p>
        
        <div style={styles.divider} />
        
        <div style={styles.fileRow}>
          <div style={styles.fileIcon}>
            <FileText size={24} />
          </div>
          <div>
            <div style={styles.fileName}>{file?.name || 'document.pdf'}</div>
            <div style={styles.fileType}>PDF Document</div>
          </div>
        </div>

        <div style={styles.inputArea}>
          <label style={styles.inputLabel}>Enter password</label>
          <div style={styles.inputRow}>
            <div style={styles.inputWrapper}>
              <Lock size={18} style={styles.lockIcon} />
              <input 
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => handlePass(e.target.value)}
                placeholder="Password"
                style={styles.input}
                onKeyDown={(e) => e.key === 'Enter' && onUnlock && onUnlock()}
              />
              <button 
                type="button"
                style={styles.eyeBtn}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            
            <button style={styles.unlockBtn} onClick={onUnlock}>
              Unlock PDF
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        <Link to="/unlock-pdf" target="_blank" style={styles.footerHint}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ShieldCheck size={18} />
          </div>
          <div>
            Forgot your password? <span style={{ fontWeight: 700, textDecoration: 'underline' }}>Unlock PDF</span>
          </div>
        </Link>
      </div>
    </div>
  );
}
